import { useCallback } from 'react';

import { Button } from 'primereact/button';
import { Editor } from 'primereact/editor';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { useCreateRequestEventMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
} from '@/rtk-query.gql';

type NewCommentProps = {
  request: RequestFragment & { events: RequestEventFragment[] };
  canManageRequest: (
    request: RequestFragment & { events: RequestEventFragment[] },
  ) => boolean;
};

export const NewComment = ({ request, canManageRequest }: NewCommentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { me } = useAppSelector((state) => state.authentication);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm<{ content: string }>();

  const [createRequestEvent] = useCreateRequestEventMutation();

  const requestId = request.id;

  const onSubmit = useCallback(
    async ({ content }: { content: string }) => {
      const res = await createRequestEvent({
        requestId,
        type: RequestEventType.COMMENT,
        content,
        refRequestId: null,
      });

      if (res.data) {
        dispatch(
          addToast({
            severity: 'success',
            summary: t('issues.reportSubmitted'),
            detail: t('issues.reportSubmittedDetail'),
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('issues.submissionFailed'),
            detail: t('issues.errorAddingComment'),
            life: ToastLife.DEFAULT,
          }),
        );
      }

      resetForm();
    },
    [createRequestEvent, dispatch, requestId, resetForm, t],
  );

  const canComment =
    canManageRequest(request) ||
    request.requestorId === me.adUserId ||
    (request.issue.__typename === 'RequestItIssue' &&
      request.issue.sponsorId === me.adUserId);

  if (!canComment) {
    return null;
  }

  return (
    <form className="new-comment" onSubmit={handleSubmit(onSubmit)}>
      <div className="new-comment__field">
        <Controller
          name="content"
          control={control}
          defaultValue=""
          rules={{ required: t('issues.commentRequired') }}
          render={({ field, fieldState }) => (
            <Editor
              id={field.name}
              {...field}
              className={classNames('new-comment__field-input', {
                'p-invalid': fieldState.error,
              })}
              onTextChange={(e) => field.onChange(e.htmlValue)}
              aria-label={t('common.comment')}
              ref={(editor) => {
                if (editor) {
                  field.ref(editor.getQuill());
                }
              }}
            />
          )}
        />

        {errors.content ? (
          <small className="p-error">{errors.content.message}</small>
        ) : null}
      </div>

      <div className="new-comment__submit">
        <Button label={t('common.comment')} type="submit" />
      </div>
    </form>
  );
};
